import React, { useRef, useState } from 'react'

function UploaderCard({ title, description, accept, apiUrl, sampleFileName, fetchJson }) {
  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [status, setStatus] = useState(null) // null | 'uploading' | 'success' | 'error'
  const [message, setMessage] = useState('')
  const [downloading, setDownloading] = useState(false)

  const handleDownloadSample = async () => {
    if (!fetchJson || !sampleFileName || downloading) return
    setDownloading(true)
    try {
      const res = await fetchJson(`/api/crm-files/crm-file/get-by-name/?file_name=${encodeURIComponent(sampleFileName)}`)
      const url = res?.file_url
      if (!url) throw new Error('No file URL returned')
      const a = document.createElement('a')
      a.href = url
      a.download = ''
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch {
      // silently fail — file might not exist yet
    } finally {
      setDownloading(false)
    }
  }

  const handleFile = (f) => {
    if (!f) return
    setFile(f)
    setStatus(null)
    setMessage('')
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files?.[0]
    if (f) handleFile(f)
  }

  const handleUpload = async () => {
    if (!file || !fetchJson) return
    setStatus('uploading')
    setMessage('')
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetchJson(apiUrl, { method: 'POST', body: formData })
      setStatus('success')
      setMessage(res?.message || res?.detail || 'File uploaded successfully.')
      setFile(null)
    } catch (err) {
      setStatus('error')
      setMessage(err?.message || 'Upload failed. Please try again.')
    }
  }

  const reset = () => {
    setFile(null)
    setStatus(null)
    setMessage('')
  }

  const fileExt = file?.name?.split('.').pop()?.toUpperCase()

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: 14,
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      maxWidth: 480,
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg, #E0F0FB, #C7E2F7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A6FA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <line x1="9" y1="15" x2="15" y2="15" />
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{title}</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{description}</div>
        </div>
        {sampleFileName && (
          <button
            onClick={handleDownloadSample}
            disabled={downloading}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
              padding: '5px 10px', border: '1px solid #D1D5DB', borderRadius: 7,
              background: '#fff', cursor: downloading ? 'not-allowed' : 'pointer',
              fontSize: 11, fontWeight: 600, color: '#374151',
              opacity: downloading ? 0.6 : 1,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {downloading ? 'Fetching…' : 'Sample Sheet'}
          </button>
        )}
      </div>

      {/* Drop zone */}
      <div
        onClick={() => !file && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragging ? '#0090D9' : file ? '#16A34A' : '#D1D5DB'}`,
          borderRadius: 10,
          padding: '28px 20px',
          textAlign: 'center',
          cursor: file ? 'default' : 'pointer',
          background: dragging ? '#F0F9FF' : file ? '#F0FDF4' : '#FAFAFA',
          transition: 'all .15s ease',
        }}
      >
        {file ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{file.name}</div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>
                {fileExt} · {(file.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); reset() }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 10px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              Drop your file here, or <span style={{ color: '#0090D9' }}>browse</span>
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Supports .xlsx, .xls, .csv</div>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files?.[0])}
      />

      {/* Status message */}
      {status === 'success' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0' }}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#16A34A" strokeWidth="2.2"><path d="M4 10l4 4 8-8"/></svg>
          <span style={{ fontSize: 12, color: '#15803D', fontWeight: 500 }}>{message}</span>
        </div>
      )}
      {status === 'error' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA' }}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#DC2626" strokeWidth="2.2"><circle cx="10" cy="10" r="8"/><path d="M10 6v4M10 14h.01"/></svg>
          <span style={{ fontSize: 12, color: '#B91C1C', fontWeight: 500 }}>{message}</span>
        </div>
      )}

      {/* Upload button */}
      <button
        onClick={handleUpload}
        disabled={!file || status === 'uploading'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '10px 0', width: '100%',
          background: !file || status === 'uploading' ? '#E5E7EB' : '#0090D9',
          color: !file || status === 'uploading' ? '#9CA3AF' : '#fff',
          border: 'none', borderRadius: 8,
          fontSize: 13, fontWeight: 700,
          cursor: !file || status === 'uploading' ? 'not-allowed' : 'pointer',
          transition: 'background .15s',
        }}
      >
        {status === 'uploading' ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            Uploading…
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Upload File
          </>
        )}
      </button>

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

export default function UploaderPage({ fetchJson }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Uploader</div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Upload data files to the system</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        <UploaderCard
          title="Upload Vehicle Data"
          description="Upload vehicle records via Excel or CSV file."
          accept=".xlsx,.xls,.csv"
          apiUrl="/api/vehicles/vehicle-manager/upload-vehicle-data/"
          sampleFileName="VEHICLE_UPLOAD_DATA"
          fetchJson={fetchJson}
        />
      </div>
    </div>
  )
}
